import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { contentApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { getErrorMessage } from '../../utils/helpers';
import ContentCard from './ContentCard';

function ContentList({ 
  showCreateButton = true, 
  limit = 20, 
  contentType = null,
  title = "Resource Library" 
}) {
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    total_pages: 1,
    has_next: false,
    has_prev: false
  });
  const [filters, setFilters] = useState({
    type: contentType,
    tags: ''
  });

  const { isAuthenticated } = useAuth();

  const fetchContent = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        page: page.toString(),
        limit: limit.toString()
      };

      if (filters.type) {
        params.type = filters.type;
      }

      if (filters.tags) {
        params.tags = filters.tags;
      }

      const response = await contentApi.getContent(params);
      
      setContent(response.content || []);
      setPagination(response.pagination || {
        page: 1,
        total_pages: 1,
        has_next: false,
        has_prev: false
      });
    } catch (err) {
      console.error('Failed to fetch content:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [filters.type, filters.tags, limit]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handlePageChange = (newPage) => {
    fetchContent(newPage);
  };

  if (loading) {
    return (
      <div className="content-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="content-list-container">
      <div className="content-header">
        <h1>{title}</h1>
        
        {showCreateButton && isAuthenticated && (
          <Link to="/content/new" className="btn btn-primary">
            Create Content
          </Link>
        )}
      </div>

      {/* Filters */}
      <div className="content-filters">
        <div className="filter-group">
          <label htmlFor="type-filter">Type:</label>
          <select
            id="type-filter"
            value={filters.type || ''}
            onChange={(e) => handleFilterChange({ type: e.target.value || null })}
            className="filter-select"
          >
            <option value="">All Types</option>
            <option value="post">Posts</option>
            <option value="page">Pages</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="tags-filter">Tags:</label>
          <input
            type="text"
            id="tags-filter"
            value={filters.tags}
            onChange={(e) => handleFilterChange({ tags: e.target.value })}
            placeholder="Filter by tags (comma-separated)"
            className="filter-input"
          />
        </div>
      </div>

      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      {content.length === 0 ? (
        <div className="no-content">
          <p>No content found.</p>
          {isAuthenticated && (
            <p>
              <Link to="/content/new" className="btn btn-primary">
                Be the first to create content!
              </Link>
            </p>
          )}
        </div>
      ) : (
        <>
          <div className="content-grid">
            {content.map((item) => (
              <ContentCard key={item.id || item._id} content={item} />
            ))}
          </div>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="pagination">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={!pagination.has_prev}
                className="btn btn-secondary"
              >
                Previous
              </button>
              
              <span className="pagination-info">
                Page {pagination.page} of {pagination.total_pages}
              </span>
              
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={!pagination.has_next}
                className="btn btn-secondary"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ContentList;